import React, { useEffect, useMemo, useState } from 'react'

import { SxProps, ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import { DashboardVariantType } from 'types/GlobalDashboard'
import {
  CustomPeriodFilter,
  isReportWidgetFullyLoaded,
  ParsedSegmentPath,
  PeriodFilter,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'
import { Context } from 'types/GlobalUserEvents'
import { isReportWidget } from 'types/GlobalReport'
import { TCommentThreadData } from 'types/GlobalComment'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useCustomization } from 'redux/hooks/Customization'
import { useTranslation } from 'language/translator'
import { formatWidgetData } from 'utils/widgetData'
import { getFilterFromSegmentPaths } from 'utils/functions'
import { Filters } from 'utils/types'

import { useReportWidget } from 'redux/hooks/Widgets'
import { useReportWidgetActions } from 'redux/actions/Widgets/ReportWidgets'
import { useDashboardFilter, usePeriodFilter } from 'redux/hooks/DashboardPage'

import Box from 'components_new/atoms/Box'

import OptionsMenu from 'components_new/molecules/OptionsMenu'
import { SETTINGS_DRAWER_SX } from 'components_new/molecules/SettingsDrawer'

import DataDialog from 'components_new/organisms/DataDialog'
import { buildKpiOptionSource } from 'components_new/organisms/DataDialog/builders'
import { resolveWidgetContextParams } from 'components_new/organisms/DataDialog/widgetContext'
import EditSegmentPopover from 'components_new/organisms/EditSegmentPopover'
import GenericWidget, {
  STANDARD_WIDGET_SCALE_FACTOR
} from 'components_new/organisms/GenericWidget'
import Types from 'components_new/organisms/Widget/Types'
import WidgetSettings from 'components_new/organisms/Widget/WidgetSettings'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import RichTextContent from './RichTextContent'
import { useWidgetContextMenu } from './useWidgetContextMenu'
import { parseQueryParams } from './utils'

type ReportWidgetProps = {
  activeFilters: Record<string, string[]>
  editMode: boolean
  editionId: string | null
  id: string
  locked?: boolean
  onAddComment?: (widgetId: string, data: TCommentThreadData) => void
  periodFilter?: PeriodFilter | CustomPeriodFilter
  reportId: string
  sx?: SxProps
  variant: 'edition' | 'template'
}

export const REPORT_WIDGET_DEFAULT_HEIGHT = 256

const ReportWidget = (props: ReportWidgetProps) => {
  const {
    activeFilters,
    editMode,
    editionId,
    id,
    locked,
    onAddComment,
    periodFilter,
    reportId,
    sx,
    variant
  } = props

  const { lang, t } = useTranslation()
  const { isAdmin } = useAccountSettings()
  const {
    tryEditDataReportWidget,
    tryGetOneReportWidget,
    tryDeleteReportWidget,
    tryUpdateReportWidget
  } = useReportWidgetActions()

  const widget = useReportWidget(id)

  // Underlying-data dialog context — mirrors what the previous
  // ResourceUnderlyingContentDialog read internally (dashboard-page state).
  const dashboardPageFilter = useDashboardFilter()
  const dashboardPagePeriodFilter = usePeriodFilter()

  const [settingsOpen, setSettingsOpen] = useState<boolean>(false)
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [underlyingContent, setUnderlyingContent] = useState<{
    widgetSettings: TWidgetSettings
    initialKpiId: string
    segmentLabel: string | number | null
    segmentFilters: Filters
    segmentPeriod: CustomPeriodFilter | null
  } | null>(null)

  const isLoaded = isReportWidget(widget)
  const isFullyLoaded = isLoaded && isReportWidgetFullyLoaded(widget)

  // Auto-fetch widget data when it becomes undefined or null
  // Listen to dataVersion instead of data to handle rapid invalidations
  useEffect(() => {
    if (!isFullyLoaded && widget) {
      tryGetOneReportWidget(
        id,
        reportId,
        parseQueryParams(activeFilters, periodFilter ?? null)
      )
    }
  }, [widget?.dataVersion])

  const handleSettings = () => {
    setSettingsOpen(true)
  }

  const handleShowUnderlyingContent = (
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => {
    if (!isReportWidget(widget)) return

    const { settings } = widget
    const initialId = initialKpiOptionId
      ? initialKpiOptionId
      : settings.kpi_options[0]?.id

    if (!initialId) return

    const segmentFilters = getFilterFromSegmentPaths(segmentPaths)

    const pathWithPeriod = segmentPaths.find((item) => item.period)
    let segmentPeriod: CustomPeriodFilter | null = null

    if (pathWithPeriod?.period) {
      segmentPeriod = {
        from: pathWithPeriod.period.from_date,
        to: pathWithPeriod.period.to_date
      }
    }

    setUnderlyingContent({
      widgetSettings: settings,
      initialKpiId: initialId,
      segmentLabel: segmentPaths[0]?.display_label ?? null,
      segmentFilters,
      segmentPeriod
    })
  }

  const isEditableWidget = editMode && variant === 'template'
  const isEditableRichText = editMode && variant === 'edition'
  const isRichText = !!widget && widget.type === WidgetType.RICH_TEXT

  const formattedData = useMemo(() => {
    if (isLoaded) {
      return formatWidgetData(widget.data, widget.settings, lang)
    }

    return null
  }, [widget])

  const fullyLoadedWidget = isFullyLoaded ? widget : null

  const {
    contextMenu,
    handleAddComment,
    handleClickSegment,
    handleKeyFigureClick,
    closeMenu,
    editPopover,
    setEditPopover,
    handleEdit,
    editPopoverValue,
    clickedSegmentLabel,
    editSegmentPopoverKey
  } = useWidgetContextMenu({
    editionId,
    formattedData,
    onAddComment,
    tryEditDataReportWidget,
    widget: fullyLoadedWidget,
    widgetId: id,
    widgetTitle: widget?.title ?? null
  })

  // mode & colors
  const customization = useCustomization()
  const chartTheme = getTheme('light', {
    ...customization,
    background_colors: null,
    text_colors: null
  })

  return (
    <>
      <Box
        key={id}
        sx={{
          flex: 1,
          minWidth: 0,
          ...(!isRichText
            ? {
                height: REPORT_WIDGET_DEFAULT_HEIGHT,
                maxHeight: REPORT_WIDGET_DEFAULT_HEIGHT,
                minHeight: REPORT_WIDGET_DEFAULT_HEIGHT
              }
            : null),
          ...(isRichText && variant === 'edition'
            ? {
                ['&:not(:only-child)']: { p: 2 }
              }
            : null),
          ...sx
        }}
      >
        <GenericWidget
          disableBorder={isRichText && variant === 'edition'}
          disablePadding={isRichText && variant === 'edition'}
          disableSubtitle={
            isRichText || (isLoaded && !widget.settings?.show_subtitle)
          }
          disableTitle={
            isRichText || (isLoaded && !widget.settings?.show_title)
          }
          editMode={isEditableWidget}
          footer={undefined} // $TODO
          fullSize={{
            active: settingsOpen,
            onClose: () => {},
            offset: {
              right: {
                maxWidth: SETTINGS_DRAWER_SX.maxWidth,
                minWidth: SETTINGS_DRAWER_SX.minWidth,
                width: SETTINGS_DRAWER_SX.width
              }
            }
          }}
          loading={isRichText ? !isLoaded : !isFullyLoaded}
          onSettings={isRichText ? undefined : handleSettings}
          options={[
            ...(isRichText
              ? []
              : [
                  {
                    iconName: 'Explore' as const,
                    onClick: () => handleShowUnderlyingContent([], null),
                    title: t('global', 'explore')
                  }
                ]),
            ...(isEditableWidget
              ? [
                  {
                    divider: true,
                    iconName: 'Delete' as const,
                    onClick: () => setDeleteOpen(true),
                    title: t('global', 'delete')
                  }
                ]
              : [])
          ]}
          subtitle={widget?.subtitle}
          title={widget?.title}
          tooltip={widget?.tooltip}
          updateWidget={(body) => tryUpdateReportWidget(id, reportId, body)}
        >
          {
            /*-- rich text --*/
            isRichText && isLoaded ? (
              <RichTextContent
                editionId={editionId}
                editMode={isEditableRichText}
                onBlur={(value) =>
                  tryUpdateReportWidget(widget.id, reportId, {
                    rich_text: value
                  })
                }
                reportId={reportId}
                value={widget.rich_text}
                variant={variant}
                widgetBlockId={widget.report_block_id}
                widgetId={widget.id}
              />
            ) : null
          }

          {
            /*-- chart --*/
            !isRichText && formattedData && isFullyLoaded ? (
              <ThemeProvider theme={chartTheme}>
                <Types
                  customSegmentBy={null} // $TODO
                  dashboardFilter={[]} // $TODO
                  fullSize={false} // $TODO
                  embedded={false} // $TODO
                  formattedData={formattedData}
                  interactive={true} // $TODO
                  isAdmin={isAdmin}
                  onKeyFigureClick={
                    variant === 'edition' ? handleKeyFigureClick : undefined
                  }
                  onSegmentClick={handleClickSegment}
                  order={{ dir: 'asc', id: null, index: null }} // $TODO
                  periodFilter={null} // $TODO
                  scaleFactor={STANDARD_WIDGET_SCALE_FACTOR}
                  setCustomSegmentBy={() => {}} // $TODO
                  setDashboardFilter={() => {}} // $TODO
                  setOrder={() => {}} // $TODO
                  setViewport={() => {}} // $TODO
                  standaloneWidget={false} // $TODO
                  variantType={DashboardVariantType.PORTAL} // $TODO
                  viewport={{
                    // $TODO
                    latitude: 0,
                    longitude: 0,
                    pitch: 0,
                    zoom: 0
                  }}
                  // eslint-disable-next-line max-len
                  widget={widget}
                  showUnderlyingContent={handleShowUnderlyingContent}
                />
              </ThemeProvider>
            ) : null
          }
        </GenericWidget>
      </Box>

      {/*-- popovers --*/}
      <OptionsMenu
        anchorPosition={
          contextMenu.event
            ? {
                left: contextMenu.event.clientX,
                top: contextMenu.event.clientY
              }
            : undefined
        }
        anchorReference="anchorPosition"
        onClose={closeMenu}
        open={contextMenu.open}
        options={[
          ...(editMode && variant === 'edition'
            ? [
                {
                  divider: contextMenu.kind === 'segment',
                  iconName: 'Edit' as const,
                  onClick: () => setEditPopover(true),
                  title: `${t('global', 'edit')} ${t('global', 'value', true)}`
                }
              ]
            : []),
          {
            iconName: 'Explore' as const,
            onClick: () =>
              contextMenu.kind === 'segment'
                ? handleShowUnderlyingContent(contextMenu.segments, null)
                : handleShowUnderlyingContent([], contextMenu.kpiOptionId),
            title: t('global', 'explore')
          },
          ...(variant === 'edition'
            ? [
                {
                  disabled: !locked,
                  iconName: 'Comment' as const,
                  onClick: handleAddComment,
                  title: t('global', 'add_comment')
                }
              ]
            : [])
        ]}
        title={contextMenu.kind === 'segment' ? clickedSegmentLabel : undefined}
      />

      {editPopover ? (
        <EditSegmentPopover
          key={
            contextMenu.kind === 'segment'
              ? editSegmentPopoverKey
              : contextMenu.kpiOptionId ?? undefined
          }
          anchorPosition={
            contextMenu.event
              ? {
                  left: contextMenu.event.clientX,
                  top: contextMenu.event.clientY
                }
              : undefined
          }
          onClose={() => setEditPopover(false)}
          onSubmit={handleEdit}
          open={true}
          title={
            contextMenu.kind === 'segment'
              ? clickedSegmentLabel
              : fullyLoadedWidget?.settings.kpi_options.find(
                (o) => o.id === contextMenu.kpiOptionId
              )?.title
          }
          value={editPopoverValue}
        />
      ) : null}

      {/*-- drawers --*/}
      {isReportWidget(widget) ? (
        <WidgetSettings
          onClose={() => setSettingsOpen(false)}
          open={settingsOpen}
          updateWidget={(body) => {
            tryUpdateReportWidget(widget.id, reportId, body)
          }} // $TODO
          widget={widget} // $TODO
        />
      ) : null}

      {/*-- dialogs --*/}
      <DeleteDialog
        onClose={() => setDeleteOpen(false)}
        onDelete={(callback) =>
          tryDeleteReportWidget(id, reportId, () => {
            callback()
          })
        }
        open={deleteOpen}
        title={widget?.title}
        type={t('dashboards', 'widget', true)}
      />

      <ThemeProvider theme={getTheme('light')}>
        <DataDialog
          source={
            underlyingContent
              ? buildKpiOptionSource(
                underlyingContent.widgetSettings.kpi_options,
                underlyingContent.initialKpiId
              )
              : null
          }
          {...(underlyingContent
            ? resolveWidgetContextParams({
              widgetSettings: underlyingContent.widgetSettings,
              dashboardFilter: dashboardPageFilter,
              periodFilter: dashboardPagePeriodFilter
            })
            : { baseFilters: undefined, basePeriod: null })}
          eventContext={{
            context: Context.FROM_REPORT_PAGE,
            reportId
          }}
          onClose={() => setUnderlyingContent(null)}
          segmentFilters={underlyingContent?.segmentFilters ?? {}}
          segmentLabel={underlyingContent?.segmentLabel ?? null}
          segmentPeriod={underlyingContent?.segmentPeriod ?? null}
        />
      </ThemeProvider>
    </>
  )
}

export default ReportWidget
